import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import Card from '../components/ui/Card';

export default function ContactUs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('contact');

  const handleBack = () => {
    // Check if user came from within the app
    const fromPath = location.state?.from;
    if (fromPath) {
      navigate(fromPath);
    } else {
      // Default to home page
      navigate('/');
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container-custom py-12">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('back')}
          </button>

          {/* Content */}
          <Card padding="lg" className="max-w-4xl mx-auto">
            {/* Logo Section */}
            <div className="flex justify-center mb-8">
              <img src="/logo.svg" alt="YOYO Club" className="h-20" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4 text-center">{t('title')}</h1>

            {/* Introduction */}
            <div className="mb-8 text-center">
              <p className="text-gray-700 text-lg">{t('intro')}</p>
            </div>

            {/* WeChat Group QR Code */}
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-4">
                <img src="/wechat-qr.jpg" alt="WeChat Group QR Code" className="w-48 h-48" />
              </div>
              <p className="text-gray-700">{t('wechat.description')}</p>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* General Inquiries */}
              <div className="p-6 bg-primary-pale rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('general.title')}</h3>
                <p className="text-gray-700 mb-3">{t('general.description')}</p>
                <a
                  href="mailto:hello@sayyoyo.com"
                  className="text-primary hover:underline font-semibold text-lg inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  hello@sayyoyo.com
                </a>
              </div>

              {/* Bug Reports */}
              <div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('bugs.title')}</h3>
                <p className="text-gray-700 mb-3">{t('bugs.description')}</p>
                <a
                  href="mailto:dev@sayyoyo.com"
                  className="text-primary hover:underline font-semibold text-lg inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  dev@sayyoyo.com
                </a>
              </div>
            </div>

            {/* Thank You Message */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 italic">{t('thankYou')}</p>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
